import { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { useRouter } from 'next/router';

import { yupResolver } from '@hookform/resolvers/yup';
import { IFormSchema } from 'schema/generator/FormSchema';
import * as Yup from 'yup';

import pages from 'constants/pages';

import { useReduxDispatch } from 'hooks/redux/useReduxDispatch';
import { useReduxSelector } from 'hooks/redux/useReduxSelector';

import ComponentToast from 'components/utils/Toast';

import { authActions } from 'store/slices/auth';
import authSelectors from 'store/slices/auth/selectors';

import FormGenerator from '../../Generator';

interface IAuthFormData {
  email: string;
  password: string;
}

const authenticateSchema = Yup.object({}).shape({
  email: Yup.string().email('invalidField').required('requiredField'),
  password: Yup.string().required('requiredField'),
});

const LoginFormOrganism = () => {
  const router = useRouter();
  const reduxDispatch = useReduxDispatch();
  const signInIsLoading = useReduxSelector(authSelectors.signInIsLoading);
  const { t } = useTranslation(['auth', 'form']);

  const formSchema = useMemo<IFormSchema>(
    () => ({
      email: {
        type: 'text',
        label: t('generalInfo.email', { ns: 'form' }),
      },
      password: {
        type: 'text',
        htmlInputType: 'password',
        label: t('login.password', { ns: 'auth' }),
      },
    }),
    [t],
  );

  const handleAuthenticate = useCallback(
    (values: IAuthFormData) => {
      reduxDispatch(
        authActions.signInRequest({
          data: {
            email: values.email,
            password: values.password,
          },
          functions: {
            error: () => {
              ComponentToast({
                title: t('login.error', { ns: 'auth' }),
                type: 'warning',
                duration: 6500,
              });
            },
            navigateTo: () => {
              router.replace(pages.home);
            },
          },
        }),
      );
    },
    [reduxDispatch, router, t],
  );

  return (
    <FormGenerator
      buttons={{
        submit: {
          text: t('login.submit', { ns: 'auth' }),
          isLoading: signInIsLoading,
          isDisabled: signInIsLoading,
        },
      }}
      formOptions={{
        resolver: yupResolver(authenticateSchema),
      }}
      handleSubmit={handleAuthenticate}
      schema={formSchema}
    />
  );
};

export default LoginFormOrganism;
